import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PROJECT_STATUSES, schemas, type Employee, type Project } from '@fpc/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { humanize } from '@/lib/format';
import { ErrorState, Modal, Pagination, Spinner, ToneBadge } from '@/components/ui';
import { CrudPage, type FieldDefinition } from '../settings/CrudPage';

const PROJECT_FIELDS: FieldDefinition[] = [
  { name: 'code', label: 'Project code', required: true },
  { name: 'name', label: 'Project name', required: true },
  { name: 'description', label: 'Description' },
  {
    name: 'status',
    label: 'Status',
    type: 'select',
    required: true,
    options: PROJECT_STATUSES.map((value) => ({ value, label: humanize(value) })),
  },
];

const PROJECT_STATUS_TONE: Record<string, 'positive' | 'neutral'> = {
  ACTIVE: 'positive',
  INACTIVE: 'neutral',
};

export function ProjectsPage() {
  const { can, companyId } = useAuth();
  const [teamProject, setTeamProject] = useState<Project | null>(null);
  return (
    <>
      <CrudPage<Project>
        title="Projects"
        subtitle="Company projects and the employees assigned to each one."
        queryKey="hr-projects"
        permissions={{ read: 'project:read', create: 'project:create', update: 'project:update' }}
        list={(query) => api.hr.projects(query)}
        create={(body) => api.hr.createProject(body)}
        update={(id, body) => api.hr.updateProject(id, body)}
        schemas={{
          create: schemas.createProjectRequest,
          update: schemas.updateProjectRequest,
        }}
        columns={[
          {
            header: 'Project',
            render: (row) => (
              <span>
                <span className="block font-medium">{row.name}</span>
                <span className="block font-mono text-xs text-ink-500">{row.code}</span>
              </span>
            ),
          },
          { header: 'Description', render: (row) => row.description || '—' },
          {
            header: 'Status',
            render: (row) => (
              <ToneBadge tone={PROJECT_STATUS_TONE[row.status] ?? 'neutral'}>
                {humanize(row.status)}
              </ToneBadge>
            ),
          },
        ]}
        fields={PROJECT_FIELDS}
        defaults={{ status: 'ACTIVE' }}
        rowActions={(row) =>
          can('project:update') && can('employee:read')
            ? [
                {
                  label: row.status === 'ACTIVE' ? 'Manage employees' : 'View employees',
                  run: async () => setTeamProject(row),
                },
              ]
            : []
        }
      />
      {teamProject && companyId ? (
        <ProjectEmployeesModal
          project={teamProject}
          companyId={companyId}
          onClose={() => setTeamProject(null)}
        />
      ) : null}
    </>
  );
}

function ProjectEmployeesModal({
  project,
  companyId,
  onClose,
}: {
  project: Project;
  companyId: string;
  onClose(): void;
}) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[] | null>(null);
  const assignments = useQuery({
    queryKey: ['project-employees', project.id],
    queryFn: () => api.hr.projectEmployees(project.id),
  });
  const employees = useQuery({
    queryKey: ['project-employee-options', companyId, page, search],
    queryFn: () =>
      api.hr.employees({ companyId, page, pageSize: 25, q: search.trim() || undefined }),
  });
  useEffect(() => {
    if (assignments.data) setSelectedIds(assignments.data.employeeIds);
  }, [assignments.data]);

  const save = useMutation({
    mutationFn: () => api.hr.updateProjectEmployees(project.id, selectedIds ?? []),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['project-employees', project.id] });
      onClose();
    },
  });

  const selected = new Set(selectedIds ?? []);
  const toggleEmployee = (employeeId: string) => {
    setSelectedIds((current) => {
      const next = new Set(current ?? assignments.data?.employeeIds ?? []);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      return [...next];
    });
  };

  return (
    <Modal title={`Employees — ${project.name}`} onClose={onClose} wide>
      <p className="mb-3 text-sm text-ink-500">
        Select the employees assigned to this project. An employee can belong to multiple projects.
      </p>
      <label className="mb-3 block">
        <span className="label">Search employees</span>
        <input
          className="input"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder="Name or employee code"
        />
      </label>

      {assignments.isLoading || employees.isLoading ? (
        <Spinner />
      ) : assignments.error ? (
        <ErrorState error={assignments.error} />
      ) : employees.error ? (
        <ErrorState error={employees.error} />
      ) : !employees.data?.items.length ? (
        <p className="py-6 text-center text-sm text-ink-500">No employees match this search.</p>
      ) : (
        <>
          <ul className="max-h-[50vh] divide-y divide-ink-100 overflow-y-auto rounded-lg border border-ink-100">
            {employees.data.items.map((employee: Employee) => (
              <li key={employee.id}>
                <label className="flex cursor-pointer items-center gap-3 px-3 py-2.5 hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={selected.has(employee.id)}
                    disabled={project.status !== 'ACTIVE'}
                    onChange={() => toggleEmployee(employee.id)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-ink-900">
                      {employee.fullName}
                    </span>
                    <span className="block font-mono text-xs text-ink-500">
                      {employee.employeeCode}
                    </span>
                  </span>
                  <ToneBadge tone={employee.status === 'ACTIVE' ? 'positive' : 'neutral'}>
                    {humanize(employee.status)}
                  </ToneBadge>
                </label>
              </li>
            ))}
          </ul>
          <Pagination
            page={employees.data.page}
            pageSize={employees.data.pageSize}
            total={employees.data.total}
            onChange={setPage}
          />
        </>
      )}

      {save.error ? <ErrorState error={save.error} /> : null}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm text-ink-500">{selectedIds?.length ?? 0} assigned</span>
        <div className="flex gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          {project.status === 'ACTIVE' ? (
            <button
              type="button"
              className="btn-primary"
              disabled={save.isPending || selectedIds === null}
              onClick={() => save.mutate()}
            >
              {save.isPending ? 'Saving…' : 'Save assignments'}
            </button>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
